import React, { useState } from 'react';
import { PhysicalButton } from './PhysicalButton';

/**
 * GoalDetailModal
 *
 * Sub-modal shown when operator presses + on a team score.
 * Collects goal details (player, minute, type) then creates a real event.
 */
export const GoalDetailModal = ({
  isOpen,
  onClose,
  teamName,
  players,
  clock,
  eventTypes,
  onConfirm,
}) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [minute, setMinute] = useState('');
  const [goalType, setGoalType] = useState('goal');

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedPlayerId(null);
      setMinute(clock?.minutes?.toString() || '');
      setGoalType('goal');
    }
  }, [isOpen, clock?.minutes]);

  if (!isOpen) return null;

  const goalTypes = [
    { value: 'goal', label: 'Goal' },
    { value: 'penalty_goal', label: 'Penalty Goal' },
    { value: 'own_goal', label: 'Own Goal' },
  ];

  // Filter active/eligible players
  const activePlayers = (players || []).filter(
    (p) => p.eligibility?.eligible && !p.eligibility?.isExpelled && !p.eligibility?.isSubstituted
  );

  const handleConfirm = () => {
    if (!selectedPlayerId) return;

    // Find the event type ID from eventTypes
    const eventTypeObj = (eventTypes || []).find(
      (et) => et.name?.toLowerCase() === goalType
    );

    onConfirm({
      playerId: selectedPlayerId,
      minute: parseInt(minute) || clock?.minutes || 1,
      half: clock?.half || 1,
      eventTypeId: eventTypeObj?.id,
      eventTypeName: goalType,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 rounded-lg shadow-2xl border border-green-600/30 max-w-md w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-green-900/50 via-gray-800 to-green-900/50 rounded-t-lg">
          <h3 className="text-lg font-bold text-green-400 tracking-wide text-center">
            ADD GOAL — {teamName}
          </h3>
        </div>

        {/* Goal Type Selector */}
        <div className="p-4 border-b border-gray-700/50">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
            Type
          </div>
          <div className="grid grid-cols-3 gap-2">
            {goalTypes.map((gt) => (
              <button
                key={gt.value}
                onClick={() => setGoalType(gt.value)}
                className={`px-2 py-2 rounded text-xs font-bold transition-colors ${
                  goalType === gt.value
                    ? 'bg-green-700 text-white border border-green-500'
                    : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
                }`}
              >
                {gt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Minute */}
        <div className="px-4 pt-3 pb-2">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">
            Minute
          </div>
          <input
            type="number"
            value={minute}
            onChange={(e) => setMinute(e.target.value)}
            min={1}
            max={120}
            className="w-20 px-3 py-2 bg-black border border-gray-600 rounded text-white text-center font-mono text-lg focus:outline-none focus:border-green-500"
          />
        </div>

        {/* Player List */}
        <div className="px-4 pb-2">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">
            Player
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 min-h-0" style={{ maxHeight: '250px' }}>
          {activePlayers.length > 0 ? (
            <div className="space-y-1">
              {activePlayers.map((pp) => {
                const player = pp.player || pp;
                const playerId = player.id || pp.player_id;
                const name = `${player.first_name || ''} ${player.last_name || ''}`.trim();
                const number = pp.jersey_number || player.jersey_number || '';
                return (
                  <button
                    key={pp.id || playerId}
                    onClick={() => setSelectedPlayerId(playerId)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded transition-colors ${
                      selectedPlayerId === playerId
                        ? 'bg-green-700/50 border border-green-500 text-white'
                        : 'bg-gray-800/50 border border-gray-700/50 text-gray-300 hover:bg-gray-700/50'
                    }`}
                  >
                    <span className="w-8 text-center font-mono font-bold text-sm text-gray-400">
                      {number}
                    </span>
                    <span className="text-sm font-medium">{name}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-500 text-sm py-4">
              No active players
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 font-semibold text-sm transition-colors"
          >
            Cancel
          </button>
          <PhysicalButton
            color="green"
            size="medium"
            onClick={handleConfirm}
            disabled={!selectedPlayerId}
          >
            Confirm Goal
          </PhysicalButton>
        </div>
      </div>
    </div>
  );
};

export default GoalDetailModal;
